import type React from 'react';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useIsExternalUser } from '~/hooks/useIsExternalUser';
import { env } from '~/lib/env';

import type { PaginationEntity } from '../components/Pagination/Pagination';
import { ListFilters } from './ListFilters';
import { Pagination } from './Pagination';
import { Toolbar } from './Toolbar';

interface ListControlsProps {
  filterType?: string;
  options?: string[];
  activeOption: boolean | null;
  setActiveOption: (option: boolean | null) => void;
  page: number;
  total: number;
  perPage: number;
  entity: PaginationEntity;
  placeholder: string;
  handlePageChange: (page: number) => void;
  toolbarRightAddon?: React.ReactNode;
}

export const ListControls: React.FC<ListControlsProps> = ({
  filterType = '',
  options = [],
  activeOption,
  setActiveOption,
  page,
  total,
  perPage,
  entity,
  placeholder,
  handlePageChange,
  toolbarRightAddon,
}) => {
  const isCurrentUserExternal = useIsExternalUser();
  const navigate = useNavigate();

  const readOnlyDisabled =
    env('VITE_CONTAINER_SETTINGS_READ_ONLY_ENABLED', false) === 'false';

  const onClickApply = useCallback(() => {
    const queryParams = new URLSearchParams();

    if (filterType === 'labelType' && activeOption !== null) {
      const labelType = activeOption ? 'PARENT_LABEL' : 'SUBLABEL';
      queryParams.set('labelType', labelType);
    }

    if (filterType === 'userType' && activeOption !== null) {
      queryParams.set('isExternal', String(activeOption));
    }
    navigate(`?${queryParams.toString()}`); // Update the URL
  }, [activeOption, navigate, filterType]);

  useEffect(() => {
    onClickApply();
  }, [activeOption, onClickApply]);

  return (
    <>
      {readOnlyDisabled && (
        <>
          <Toolbar placeholder={placeholder} rightAddon={toolbarRightAddon} />
          <div className="flex justify-between">
            {isCurrentUserExternal && (
              <ListFilters
                options={options}
                activeOption={activeOption}
                setActiveOption={setActiveOption}
              />
            )}
            <Pagination
              page={page}
              total={total}
              perPage={perPage}
              entity={entity}
              handlePageChange={handlePageChange}
            />
          </div>
        </>
      )}
    </>
  );
};

ListControls.displayName = 'ListControls';
